import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ASSETS } from '../assets/assets';
import { SectionTitle, CloudScroll } from './ChineseDecor';
import Lightbox from './Lightbox';
import { getLocalizedText } from '../cms/localization';
import type { SupportedLang } from '../cms/localization';

interface LocalizedField { 'zh-HK'?: string; 'zh-CN'?: string; en?: string; }
interface Stat { num: string; label: string | LocalizedField; }
interface GalleryItem { src: string; caption: string | LocalizedField; }

interface AboutUsProps {
  content?: Record<string, unknown>;
}

const AboutUs: React.FC<AboutUsProps> = ({ content = {} }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;

  const eyebrow = getLocalizedText(content.eyebrow, lang, t('about.eyebrow'));
  const title = getLocalizedText(content.title, lang, t('about.title'));
  const p1 = getLocalizedText(content.p1, lang, t('about.p1'));
  const p2 = getLocalizedText(content.p2, lang, t('about.p2'));

  const legacyStats = t('about.stats', { returnObjects: true }) as Array<{ num: string; label: string }>;
  const legacyGallery = t('about.gallery', { returnObjects: true }) as Array<{ caption: string }>;

  const cmsStats = (content.stats as Stat[] | undefined) || legacyStats;
  const cmsGallery = (content.gallery as GalleryItem[] | undefined) || [
    { src: ASSETS.aboutExhibition, caption: legacyGallery?.[0]?.caption || '' },
    { src: ASSETS.aboutInterview, caption: legacyGallery?.[1]?.caption || '' },
    { src: ASSETS.aboutMaster, caption: legacyGallery?.[2]?.caption || '' },
  ];

  const galleryImgs = [
    { src: cmsGallery[0]?.src || ASSETS.aboutExhibition, caption: getLocalizedText(cmsGallery[0]?.caption, lang, legacyGallery?.[0]?.caption) },
    { src: cmsGallery[1]?.src || ASSETS.aboutInterview, caption: getLocalizedText(cmsGallery[1]?.caption, lang, legacyGallery?.[1]?.caption) },
    { src: cmsGallery[2]?.src || ASSETS.aboutMaster, caption: getLocalizedText(cmsGallery[2]?.caption, lang, legacyGallery?.[2]?.caption) },
  ];

  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  return (
    <section id="about" className="relative py-12 md:py-16 bg-xuan overflow-hidden">
      {/* faint cloud texture */}
      <div className="absolute inset-0 bg-cloud opacity-[0.5] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text */}
          <div>
            <SectionTitle eyebrow={eyebrow} title={title} align="left" />
            <p className="mt-8 text-brand-ink/75 font-sans leading-loose text-[15px]">{p1}</p>
            <p className="mt-5 text-brand-ink/75 font-sans leading-loose text-[15px]">{p2}</p>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-12">
              {cmsStats.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center sm:text-left border-t border-brand-gold/40 pt-4"
                >
                  <div className="text-2xl md:text-3xl font-serif text-brand-cinnabar">{s.num}</div>
                  <div className="text-xs font-sans text-brand-ink/60 mt-1 tracking-wide">{getLocalizedText(s.label, lang, legacyStats?.[i]?.label)}</div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Photo gallery — uniform 4:3 grid with click-to-enlarge */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              {/* Exhibition — portrait, spans full height */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="row-span-2 relative overflow-hidden lattice-border p-1.5 cursor-pointer group"
                onClick={() => setLightboxSrc(galleryImgs[0].src)}
              >
                <div className="aspect-[3/4] h-full">
                  <img
                    src={galleryImgs[0].src}
                    alt={galleryImgs[0].caption}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="absolute bottom-0 inset-x-0 bg-brand-ink/70 text-brand-paper/90 text-[11px] font-sans px-3 py-2 leading-snug">
                  {galleryImgs[0].caption}
                </p>
              </motion.div>

              {/* Interview & Master — landscape */}
              {[1, 2].map((idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: idx * 0.1 }}
                  className="relative overflow-hidden lattice-border p-1.5 cursor-pointer group"
                  onClick={() => setLightboxSrc(galleryImgs[idx].src)}
                >
                  <div className="aspect-[4/3]">
                    <img
                      src={galleryImgs[idx].src}
                      alt={galleryImgs[idx].caption}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <p className="absolute bottom-0 inset-x-0 bg-brand-ink/70 text-brand-paper/90 text-[10px] font-sans px-2 py-1.5 leading-tight">
                    {galleryImgs[idx].caption}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-20">
          <CloudScroll className="w-24 h-10 text-brand-gold/50" />
        </div>
      </div>

      <Lightbox
        src={lightboxSrc || ''}
        isOpen={!!lightboxSrc}
        onClose={() => setLightboxSrc(null)}
      />
    </section>
  );
};

export default AboutUs;
